import React, { useState } from 'react';
import BaseModal from '../../../../components/modals/BaseModal';
import { useLoadingContext } from '../../../../context/loading/LoadingContext';
import { useUserContext } from '../../../../context/user_context/UserContext';
import { Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, IconButton, InputAdornment, Typography } from '@mui/material';
import { useToast, ToastType } from '../../../../context/toast/ToastContext';
import { StringKeys } from '../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { addInventoryOrder } from '../../../../repository/OrdersDAO';
import { IconPlus } from '@tabler/icons-react';
import { useInventoryContext } from '../../../../context/inventory_context/InventoryContext';
import { Delete } from '@mui/icons-material';

const AddInventoryOrderModal = ({ show, onHide, onCreate, inventoryItems }) => {

    const createEmptyLineItem = () => ({
        inventory_item_id: "",
        quantity_ordered: "",
        price_per_unit: 0,
        manuallyUpdated: false,
        quantity_error: false
    });

    const [lineItems, setLineItems] = useState([createEmptyLineItem()]);
    const [supplier, setSupplier] = useState("");
    const [status, setStatus] = useState("pending");
    const [expectedDeliveryDate, setExpectedDeliveryDate] = useState("");
    const [totalCost, setTotalCost] = useState(0);
    const [notes, setNotes] = useState("");
    const [purchaseOrder, setPurchaseOrder] = useState("");
    const { setLoadingData } = useLoadingContext();
    const { firebaseUser } = useUserContext();
    const { suppliers } = useInventoryContext();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [taxType, setTaxType] = useState('percentage');
    const [taxValue, setTaxValue] = useState(0);
    const [taxAmount, setTaxAmount] = useState(0);
    const [errors, setErrors] = useState({
        expectedDeliveryDate: '',
        lineItems: ''
    });

    const handleAddLineItem = () => {
        setLineItems([...lineItems, createEmptyLineItem()]);
    };

    const handleRemoveLineItem = (index) => {
        const newLineItems = lineItems.filter((_, i) => i !== index);
        setLineItems(newLineItems);
        calculateTotalCost(newLineItems);
    };

    const handleLineItemChange = (index, key, value) => {
        const newLineItems = [...lineItems];

        if (key === 'inventory_item_id') {
            const selectedItem = inventoryItems.find(item => item.id === value);
            if (selectedItem && !newLineItems[index].manuallyUpdated) {
                newLineItems[index].price_per_unit = selectedItem.price || 0;
            }
            newLineItems[index][key] = value;
        } else if (key === 'quantity_ordered') {
            // Allow empty value for quantity
            newLineItems[index][key] = value;
            // Clear error if previously shown
            if (value !== "" && !isNaN(value) && parseFloat(value) > 0) {
                newLineItems[index].quantity_error = false;
            }
        } else {
            newLineItems[index][key] = key === 'price_per_unit' 
                ? parseFloat(value) || 0
                : value;
            if (key === 'price_per_unit') {
                newLineItems[index].manuallyUpdated = true;
            }
        }
        setLineItems(newLineItems);
        calculateTotalCost(newLineItems);
    };

    const calculateTotalWithTax = (subtotal) => {
        if (taxType === 'fixed') {
            const exactTax = parseFloat(taxValue) || 0;
            setTaxAmount(exactTax);
            setTotalCost(subtotal + exactTax);
            return;
        }
        
        const percentageTax = (subtotal * (parseFloat(taxValue) || 0)) / 100;
        setTaxAmount(percentageTax);
        setTotalCost(subtotal + percentageTax);
    };

    const calculateTotalCost = (items) => {
        const subtotal = items.reduce((acc, item) => {
            const quantity = parseFloat(item.quantity_ordered) || 0;
            const price = parseFloat(item.price_per_unit) || 0;
            return acc + (quantity * price);
        }, 0);
        calculateTotalWithTax(subtotal);
    };

    const handleTaxChange = (value) => {
        const newTaxValue = parseFloat(value) || 0;
        setTaxValue(newTaxValue);
        
        const subtotal = lineItems.reduce((acc, item) => {
            const quantity = parseFloat(item.quantity_ordered) || 0;
            const price = parseFloat(item.price_per_unit) || 0;
            return acc + (quantity * price);
        }, 0);
        
        if (taxType === 'fixed') {
            setTaxAmount(newTaxValue);
            setTotalCost(subtotal + newTaxValue);
        } else {
            calculateTotalWithTax(subtotal);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        let isValid = true;

        if (!expectedDeliveryDate) {
            newErrors.expectedDeliveryDate = t(StringKeys.EXPECTED_DELIVERY_DATE_REQUIRED);
            isValid = false;
        }

        // Validate line items
        let hasValidLineItems = false;
        const newLineItems = [...lineItems];
        let lineItemsValid = true;

        newLineItems.forEach((item, index) => {
            // Check for valid quantity
            if (item.inventory_item_id && (item.quantity_ordered === "" || isNaN(item.quantity_ordered) || parseFloat(item.quantity_ordered) <= 0)) {
                newLineItems[index].quantity_error = true;
                lineItemsValid = false;
            } else if (item.inventory_item_id && parseFloat(item.quantity_ordered) > 0 && parseFloat(item.price_per_unit) > 0) {
                hasValidLineItems = true;
            }
        });

        if (!hasValidLineItems) {
            newErrors.lineItems = t(StringKeys.AT_LEAST_ONE_LINE_ITEM_IS_REQUIRED);
            isValid = false;
        }

        if (!lineItemsValid) {
            newErrors.quantityError = t(StringKeys.QUANTITY_IS_REQUIRED);
            isValid = false;
        }

        setLineItems(newLineItems);
        setErrors(newErrors);
        return isValid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        setLoadingData(true);
        try {
            const itemsMap = lineItems.reduce((acc, item) => {
                if (item.inventory_item_id && parseFloat(item.quantity_ordered) > 0) {
                    acc[item.inventory_item_id] = {
                        price_per_unit: parseFloat(item.price_per_unit),
                        quantity_ordered: parseFloat(item.quantity_ordered),
                        quantity_received: 0,
                    };
                }
                return acc;
            }, {});

            const order = {
                supplier,
                status,
                order_date: new Date(),
                expected_delivery_date: new Date(expectedDeliveryDate),
                total_cost: parseFloat(totalCost),
                tax_type: taxType,
                tax_value: parseFloat(taxValue),
                tax_amount: parseFloat(taxAmount),
                purchase_order: purchaseOrder,
                created_by: firebaseUser.uid,
                notes,
                items: itemsMap
            };

            const result = await addInventoryOrder(firebaseUser.organization, order);

            if (result.success) {
                showToast(StringKeys.ORDER_CREATED_SUCCESSFULLY, ToastType.SUCCESS);
                resetForm();
                onHide();
                if (onCreate) onCreate(result);
            } else {
                showToast(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN, ToastType.DANGER);
            }
        } catch (error) {
            showToast(StringKeys.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN, ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };

    const resetForm = () => {
        setLineItems([createEmptyLineItem()]);
        setSupplier("");
        setStatus("pending");
        setExpectedDeliveryDate("");
        setTotalCost(0);
        setNotes("");
        setPurchaseOrder("");
        setTaxType('percentage');
        setTaxValue(0);
        setTaxAmount(0);
        setErrors({});
    };

    const actions = (
        <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    );

    return (
        <BaseModal
            open={show}
            onClose={() => {
                resetForm();
                onHide();
            }}
            title={t(StringKeys.ADD_ORDER)}
            actions={actions}
        >
            <Grid container spacing={2}>
                <Grid item xs={12} md={8}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="supplier-label">{t(StringKeys.SUPPLIER)}</InputLabel>
                        <Select
                            labelId="supplier-label"
                            id="supplier-select"
                            value={supplier}
                            label={t(StringKeys.SUPPLIER)}
                            onChange={(e) => setSupplier(e.target.value)}
                        >
                            {suppliers.map((supplier) => (
                                <MenuItem key={supplier.name} value={supplier.name}>{supplier.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
                    <TextField
                        margin="dense"
                        id="purchase-order"
                        label={t(StringKeys.PURCHASE_ORDER)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={purchaseOrder}
                        onChange={(e) => setPurchaseOrder(e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
                    <TextField
                        margin="dense"
                        id="purchase-order-mobile"
                        label={t(StringKeys.PURCHASE_ORDER)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={purchaseOrder}
                        onChange={(e) => setPurchaseOrder(e.target.value)}
                    />
                </Grid>
                {lineItems.map((item, index) => (
                    <React.Fragment key={index}>
                        <Grid item xs={12}>
                            <Typography 
                                variant="subtitle1" 
                                sx={{ 
                                    mt: 2,
                                    mb: 1,
                                    fontWeight: 'medium',
                                    color: 'text.secondary'
                                }}
                            >
                                {t(StringKeys.ITEM)} {index + 1}
                            </Typography>
                        </Grid>
                        <Grid container item xs={12} spacing={2}>
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth margin="dense">
                                    <InputLabel id={`item-label-${index}`}>{t(StringKeys.ITEM_NAME)}</InputLabel>
                                    <Select
                                        labelId={`item-label-${index}`}
                                        id={`item-select-${index}`}
                                        value={item.inventory_item_id}
                                        label={t(StringKeys.ITEM_NAME)}
                                        onChange={(e) => handleLineItemChange(index, 'inventory_item_id', e.target.value)}
                                    >
                                        {inventoryItems.map((invItem) => (
                                            <MenuItem key={invItem.id} value={invItem.id}>{invItem.name}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    margin="dense"
                                    id={`quantity-${index}`}
                                    label={t(StringKeys.QUANTITY)}
                                    type="number"
                                    InputProps={{ 
                                        inputProps: { 
                                            min: "0", 
                                            step: "1",
                                            onKeyDown: (e) => {
                                                if (["e", "E", "+", "-"].includes(e.key)) {
                                                    e.preventDefault();
                                                }
                                            }
                                        } 
                                    }}
                                    fullWidth
                                    variant="outlined"
                                    value={item.quantity_ordered}
                                    onChange={(e) => handleLineItemChange(index, 'quantity_ordered', e.target.value)}
                                    error={item.quantity_error}
                                    helperText={item.quantity_error ? t(StringKeys.QUANTITY_IS_REQUIRED) : ""}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <TextField
                                    margin="dense"
                                    id={`price-${index}`}
                                    label={t(StringKeys.PRICE_PER_UNIT)}
                                    type="number"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                        inputProps: { 
                                            min: "0", 
                                            step: "0.01",
                                            pattern: "^\\d*(\\.\\d{0,2})?$"
                                        }
                                    }}
                                    fullWidth
                                    variant="outlined"
                                    value={typeof item.price_per_unit === 'number' ? item.price_per_unit.toFixed(2) : item.price_per_unit}
                                    onChange={(e) => {
                                        const value = parseFloat(e.target.value);
                                        handleLineItemChange(index, 'price_per_unit', isNaN(value) ? 0 : Math.round(value * 100) / 100);
                                    }}
                                    onFocus={() => {
                                        const newLineItems = [...lineItems];
                                        newLineItems[index].manuallyUpdated = true;
                                        setLineItems(newLineItems);
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12} md={2} container alignItems="center">
                                <Button 
                                    onClick={() => handleRemoveLineItem(index)}
                                    sx={{ 
                                        display: { xs: 'flex', md: 'none' },
                                        width: '100%'
                                    }}
                                    variant="contained"
                                    color="error"
                                >
                                    {t(StringKeys.DELETE_ITEM_X, {x:`${index + 1}`})}
                                </Button>
                                
                                <IconButton 
                                    onClick={() => handleRemoveLineItem(index)}
                                    sx={{ 
                                        display: { xs: 'none', md: 'flex' },
                                        marginTop: 1
                                    }}
                                >
                                    <Delete sx={{ color: "red" }} />
                                </IconButton>
                            </Grid>
                        </Grid>
                    </React.Fragment>
                ))}

                <Grid item xs={12}>
                    <Button onClick={handleAddLineItem}>
                        <IconPlus /> {t(StringKeys.ADD_LINE_ITEM)}
                    </Button>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        margin="dense"
                        id="expectedDeliveryDate"
                        label={t(StringKeys.EXPECTED_DELIVERY_DATE)}
                        type="date"
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        variant="outlined"
                        value={expectedDeliveryDate}
                        onChange={(e) => {
                            setExpectedDeliveryDate(e.target.value);
                            setErrors(prev => ({ ...prev, expectedDeliveryDate: '' }));
                        }}
                        error={!!errors.expectedDeliveryDate}
                        helperText={errors.expectedDeliveryDate}
                    />
                </Grid>
                <Grid item xs={12} md={6} sx={{ display: { xs: 'none', md: 'block' } }}></Grid>
            </Grid>
            
            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="tax-type-label">{t(StringKeys.TAX_TYPE)}</InputLabel>
                        <Select
                            labelId="tax-type-label"
                            id="tax-type"
                            value={taxType}
                            label={t(StringKeys.TAX_TYPE)}
                            onChange={(e) => {
                                setTaxType(e.target.value);
                                setTaxValue(0);
                                setTaxAmount(0);
                                const subtotal = lineItems.reduce((acc, item) => 
                                    acc + (parseFloat(item.quantity_ordered) * parseFloat(item.price_per_unit)), 0);
                                setTotalCost(subtotal);
                            }}
                        >
                            <MenuItem value="percentage">{t(StringKeys.PERCENTAGE)}</MenuItem>
                            <MenuItem value="fixed">{t(StringKeys.FIXED_AMOUNT)}</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                
                <Grid item xs={12} md={4}>
                    <TextField
                        margin="dense"
                        id="taxValue"
                        label={taxType === 'percentage' ? t(StringKeys.TAX_PERCENTAGE) : t(StringKeys.TAX_AMOUNT)}
                        type="number"
                        InputProps={{
                            startAdornment: taxType === 'fixed' ? 
                                <InputAdornment position="start">$</InputAdornment> :
                                undefined,
                            endAdornment: taxType === 'percentage' ? 
                                <InputAdornment position="end">%</InputAdornment> :
                                undefined,
                            inputProps: { 
                                min: "0", 
                                step: taxType === 'percentage' ? "1" : "0.01",
                                pattern: taxType === 'percentage' ? "^\\d*$" : "^\\d*(\\.\\d{0,2})?$"
                            }
                        }}
                        fullWidth
                        variant="outlined"
                        value={taxType === 'fixed' ? taxValue.toFixed(2) : taxValue}
                        onChange={(e) => handleTaxChange(e.target.value)}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextField
                        margin="dense"
                        id="calculatedTax"
                        label={t(StringKeys.TAX_AMOUNT)}
                        type="number"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { 
                                readOnly: true
                            }
                        }}
                        fullWidth
                        variant="outlined"
                        value={typeof taxAmount === 'number' ? taxAmount.toFixed(2) : taxAmount}
                    />
                </Grid>
                
                <Grid item xs={12}>
                    <TextField
                        margin="dense"
                        id="totalCost"
                        label={t(StringKeys.TOTAL_COST)}
                        type="number"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: { 
                                min: "0", 
                                step: "0.01",
                                readOnly: true
                            }
                        }}
                        fullWidth
                        variant="outlined"
                        value={typeof totalCost === 'number' ? totalCost.toFixed(2) : totalCost}
                    />
                </Grid>
            </Grid>
            {/* <TextField
                margin="dense"
                id="notes"
                label={t(StringKeys.NOTES)}
                type="text"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
            /> */}
            {errors.lineItems && (
                <Grid item xs={12}>
                    <Typography color="error" variant="caption">
                        {errors.lineItems}
                    </Typography>
                </Grid>
            )}
        </BaseModal>
    );
};

export default AddInventoryOrderModal;
